/**
 * The Profile view of the Tax Profile page: the values only, for a person who uses the profile as
 * a reference. TaxProfile.tsx draws it while the toggle at the top of the page is on Profile, and
 * the Review view (everything else on the page) while it is on Review.
 *
 * Expects the catalog sections with their entries, the values the api sent keyed by catalog key,
 * the key of the section that draws the year switch with the switch's years, its year and its
 * callback, the owners, officers, subsidiaries and state registrations rows, how many values and
 * table rows wait for a person (the page's waitingCount), the callback that switches to the Review
 * view, and the callback that switches to it and scrolls to one row.
 *
 * Renders, under the line "Not reviewed yet: N" with its "Review them" button (left out when N is
 * 0), one card per catalog section in a grid (one column, two from md, three from xl), each with
 * the section's heading (sectionHeading in ProfileGroup.tsx) and one label and value per catalog
 * entry, and then a table per list, with the same columns the review view's tables draw and no
 * Approval column. A value's text is the one FieldRow shows (formatFieldValue of shownValue),
 * NOT_ON_FILE for an empty value; a list cell's is the one ListTable's cell shows (the column's
 * `text`, else defaultText, or the column's `render`).
 *
 * A value nobody has reviewed yet ('proposed') is drawn faint (opacity-60), followed by a
 * exclamation-mark icon whose tooltip says it is not reviewed yet and names its source as the Where it
 * came from column does (notReviewedTip). An approved value a source gave a different value for
 * ('changed') shows the approved value as usual, followed by the same icon with
 * DIFFERENT_VALUE_WAITING_TIP. The icon is a button: it switches to the Review view and scrolls to
 * the value's row (fieldRowElementId) or the list row (listRowElementId), which TaxProfile.tsx does
 * with the same pendingGoToId as "Go to field". A list cell takes the same treatment from its own
 * status; for a changed cell the approved value is shown, by handing ListTable's helpers the
 * decision a person makes when keeping the approved value ({ kind: 'keep' }).
 *
 * This view writes nothing, opens no window and has no filters.
 */

import type { ReactNode } from 'react';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  TAX_PROFILE_FIELD_GROUPS,
  TaxProfileFieldDefinition,
} from '@shared/constants/taxProfileFields';
import type {
  TaxProfileFieldValue,
  TaxProfileListRow,
} from '@shared/types/taxProfile';
import { formatFieldValue } from '@/pages/TaxProfile/format';
import { shownValue } from '@/pages/TaxProfile/fieldState';
import { listColumn, rowCellValue } from '@/pages/TaxProfile/listRows';
import {
  DIFFERENT_VALUE_WAITING_TIP,
  LIST_EMPTY_SENTENCES,
  TAX_YEAR_BOX_HEADING,
  VIEW_WORDS,
  notReviewedLine,
  notReviewedTip,
} from '@/pages/TaxProfile/wording';
import type { ListRowNoun } from '@/pages/TaxProfile/wording';
import { fieldRowElementId } from '@/pages/TaxProfile/components/FieldRow';
import {
  ListCellContext,
  ListTableColumn,
  defaultText,
  listRowElementId,
} from '@/pages/TaxProfile/components/ListTable';
import { officerColumns } from '@/pages/TaxProfile/components/OfficersTable';
import { OWNER_COLUMNS } from '@/pages/TaxProfile/components/OwnersTable';
import {
  STATE_REGISTRATION_COLUMNS,
  SUBSIDIARY_COLUMNS,
} from '@/pages/TaxProfile/components/SubsidiariesTable';
import { TaxYearSwitch } from '@/pages/TaxProfile/components/ProfileGroup';
import type { FieldDecision } from '@/pages/TaxProfile/components/ValuePicker';
import {
  BRAND_BORDER_CLASS,
  BRAND_HEADING_CLASS,
} from '@/pages/TaxProfile/brand';

export interface ProfileViewProps {
  /** The catalog sections that have entries, in catalog order, as TaxProfile.tsx builds them. */
  definitionsByGroup: ReadonlyArray<{
    group: (typeof TAX_PROFILE_FIELD_GROUPS)[number];
    definitions: ReadonlyArray<TaxProfileFieldDefinition>;
  }>;
  fieldsByKey: Record<string, TaxProfileFieldValue>;
  taxYears: number[];
  taxYear: number | null;
  onTaxYearChange: (taxYear: number) => void;
  /** True while the answer for the year just picked is on its way. */
  taxYearLoading?: boolean;
  owners: TaxProfileListRow[];
  officers: TaxProfileListRow[];
  subsidiaries: TaxProfileListRow[];
  stateTaxIds: TaxProfileListRow[];
  /** Every value and table row that waits for a person: TaxProfile.tsx's waitingCount. */
  waitingCount: number;
  /** Switches the page to the Review view. */
  onReview: () => void;
  /** Switches the page to the Review view and scrolls to the element with this id. */
  onGoToElement: (elementId: string) => void;
}

/** A card: the theme's card color, with the brand's thin green border (brand.ts). */
const CARD_CLASS = `rounded-lg p-3 min-w-0 bg-base-200 ${BRAND_BORDER_CLASS}`;
const CARD_HEADING_CLASS = `font-bold mb-2 ${BRAND_HEADING_CLASS}`;

/** The faint text FieldRow draws an empty value in, which this view draws "Not on file" in too. */
const FAINT_VALUE_CLASS = 'opacity-60';

/**
 * A value nobody has reviewed yet: fainter than "Not on file", and in italics, so that it reads as
 * different from both an approved value and an empty one.
 */
const NOT_REVIEWED_VALUE_CLASS = 'opacity-40 italic';

/** The icon's button: small and round, in the theme's warning yellow, with the page's focus ring. */
const ICON_BUTTON_CLASS =
  'btn btn-ghost btn-xs btn-circle min-h-0 h-5 w-5 text-warning ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

const NOT_REVIEWED_TOOLTIP_CLASS = [
  'tooltip tooltip-top whitespace-normal text-left',
  'before:left-auto before:right-0 before:translate-x-0',
  'before:max-w-[10rem] md:before:max-w-[14rem]',
  'focus-within:before:opacity-100 focus-within:after:opacity-100',
  'before:content-[attr(data-tip)]',
].join(' ');

/**
 * The tooltip classes ShownValue puts on a value and its icon together, so the tooltip shows while
 * either is hovered. The tooltip is daisyUI's, as the US person icon in OwnersTable.tsx draws it;
 * the focus-within classes show it while the icon has the keyboard focus too. A Tailwind before:
 * variant also sets `content` to an empty string, on hover as well as on focus, which would blank
 * the text daisyUI puts there, so the last class sets it back to the data-tip text. It opens above the icon with its right edge
 * at the icon, at most 10rem wide on a phone and 14rem from md up: centered, a tooltip of an icon
 * at the right of a card reaches past the screen's right edge on a phone and makes the page scroll
 * sideways even while it is hidden, and opening to the left cuts off the tooltip of an icon in the
 * middle of the screen.
 *
 * The exclamation-mark icon after a value that waits for a person is a button whose accessible name
 * is the tooltip's text, and a click calls onClick.
 */
function NotReviewedIcon({
  tip,
  onClick,
  testId,
}: {
  tip: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type='button'
      aria-label={tip}
      data-testid={testId}
      className={ICON_BUTTON_CLASS}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={faCircleExclamation} aria-hidden />
    </button>
  );
}

/**
 * The tooltip of a value's icon: notReviewedTip for a proposed value, DIFFERENT_VALUE_WAITING_TIP
 * for a changed one, null for a value that waits for nobody.
 */
const tipOf = (field: TaxProfileFieldValue | undefined): string | null => {
  if (field?.status === 'proposed') return notReviewedTip(field, field.key);
  if (field?.status === 'changed') return DIFFERENT_VALUE_WAITING_TIP;
  return null;
};

/** A value's text, faint when nobody has reviewed it, and the icon when it waits for a person. */
function ShownValue({
  text,
  field,
  elementId,
  testId,
  onGoToElement,
  children,
}: {
  text: string;
  field: TaxProfileFieldValue | undefined;
  elementId: string;
  testId: string;
  onGoToElement: (elementId: string) => void;
  /** Drawn instead of the text, as the US person column's icon. */
  children?: ReactNode;
}) {
  const tip = tipOf(field);
  let valueClass = '';
  if (field?.status === 'proposed') valueClass = NOT_REVIEWED_VALUE_CLASS;
  if (field?.status === 'empty') valueClass = FAINT_VALUE_CLASS;
  return (
    <span
      className={`inline-flex flex-wrap items-center gap-x-1 max-w-full ${
        tip ? NOT_REVIEWED_TOOLTIP_CLASS : ''
      }`}
      data-tip={tip ?? undefined}
    >
      <span
        className={`break-words max-w-full ${valueClass}`}
        data-testid={testId}
      >
        {children ?? text}
      </span>
      {tip && (
        <NotReviewedIcon
          tip={tip}
          onClick={() => onGoToElement(elementId)}
          testId={`${testId}-review`}
        />
      )}
    </span>
  );
}

/** The decision that makes ListTable's helpers show a changed cell's approved value. */
const KEEP_APPROVED: FieldDecision = { kind: 'keep' };

/** One list, read only: the review view's columns, one row per list row, no Approval column. */
function ReadOnlyListTable({
  label,
  noun,
  rows,
  columns,
  onGoToElement,
}: {
  label: string;
  noun: ListRowNoun;
  rows: TaxProfileListRow[];
  columns: ListTableColumn[];
  onGoToElement: (elementId: string) => void;
}) {
  return (
    <div className='overflow-x-auto rounded-lg'>
      <table className='table table-compact w-full' aria-label={label}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.align === 'right' ? 'text-right' : ''}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.recordId}
              data-testid={`tax-profile-view-row-${row.recordId}`}
            >
              {columns.map((column, index) => {
                const cell = row.cells[column.key];
                const decision =
                  cell?.status === 'changed' ? KEEP_APPROVED : undefined;
                const context: ListCellContext = {
                  value: rowCellValue(cell, decision),
                  cell,
                  decision,
                };
                const text = (
                  column.text ?? defaultText(listColumn(column.key))
                )(context);
                return (
                  <td
                    key={column.key}
                    className={`${
                      column.align === 'right' ? 'text-right' : ''
                    } ${index === 0 ? 'font-bold' : ''}`}
                  >
                    <ShownValue
                      text={text}
                      field={cell}
                      elementId={listRowElementId(noun, row.recordId)}
                      testId={`tax-profile-view-cell-${row.recordId}-${column.key}`}
                      onGoToElement={onGoToElement}
                    >
                      {column.render ? column.render(context) : undefined}
                    </ShownValue>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A list's heading and its table, or the sentence that says it has no rows. */
function ListSection({
  heading,
  emptySentence,
  children,
  isEmpty,
}: {
  heading: string;
  emptySentence: string;
  children: ReactNode;
  isEmpty: boolean;
}) {
  return (
    <section className={CARD_CLASS}>
      <h3 className={CARD_HEADING_CLASS}>{heading}</h3>
      {isEmpty ? (
        <p className='text-xs opacity-70'>{emptySentence}</p>
      ) : (
        children
      )}
    </section>
  );
}

export default function ProfileView({
  definitionsByGroup,
  fieldsByKey,
  taxYears,
  taxYear,
  onTaxYearChange,
  taxYearLoading = false,
  owners,
  officers,
  subsidiaries,
  stateTaxIds,
  waitingCount,
  onReview,
  onGoToElement,
}: ProfileViewProps) {
  // One card: the section's heading, and a label and a value per catalog entry the page has.
  const fieldCard = ({
    group,
    definitions,
  }: ProfileViewProps['definitionsByGroup'][number]) => (
    <section key={group.key} className={CARD_CLASS} aria-label={group.label}>
      <h3 className={CARD_HEADING_CLASS}>{group.label}</h3>
      <dl className='grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-3 gap-y-1.5 text-sm'>
        {definitions
          .filter((definition) => fieldsByKey[definition.key])
          .map((definition) => {
            const field = fieldsByKey[definition.key];
            return (
              <div key={definition.key} className='contents'>
                <dt className='opacity-70 break-words'>{definition.label}</dt>
                <dd className='min-w-0'>
                  <ShownValue
                    text={formatFieldValue(shownValue(field), definition)}
                    field={field}
                    elementId={fieldRowElementId(definition.key)}
                    testId={`tax-profile-view-value-${definition.key}`}
                    onGoToElement={onGoToElement}
                  />
                </dd>
              </div>
            );
          })}
      </dl>
    </section>
  );

  // The sections holding a value kept per tax year, which the year switch changes.
  const perYearGroups = definitionsByGroup.filter(({ definitions }) =>
    definitions.some((definition) => definition.yearly)
  );
  const otherGroups = definitionsByGroup.filter(
    (entry) => !perYearGroups.includes(entry)
  );

  return (
    <div className='flex flex-col gap-4 min-w-0' data-testid='tax-profile-view'>
      {waitingCount > 0 && (
        <p className='text-xs opacity-70 flex flex-wrap items-center gap-2'>
          {notReviewedLine(waitingCount)}
          <button
            type='button'
            className='link link-primary link-hover'
            onClick={onReview}
          >
            {VIEW_WORDS.reviewThem}
          </button>
        </p>
      )}

      {/* The cards of one row stretch to the row's height, so the cards of a row are the same size
          and no gap is left under a shorter one, as in the reference view the layout came from. */}
      <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
        {otherGroups.map(fieldCard)}
      </div>

      {/* Everything the year switch changes sits in one box under it: the sections holding values
          kept per tax year, and the owners, whose shares come from k1_partners_tax_years. */}
      <section
        className={`rounded-lg p-3 flex flex-col gap-4 min-w-0 ${BRAND_BORDER_CLASS}`}
        aria-label={TAX_YEAR_BOX_HEADING}
      >
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <h2 className={`font-bold ${BRAND_HEADING_CLASS}`}>
            {TAX_YEAR_BOX_HEADING}
          </h2>
          {taxYears.length > 0 && (
            <TaxYearSwitch
              taxYears={taxYears}
              taxYear={taxYear}
              onTaxYearChange={onTaxYearChange}
              taxYearLoading={taxYearLoading}
            />
          )}
        </div>
        {perYearGroups.length > 0 && (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {perYearGroups.map(fieldCard)}
          </div>
        )}
        <ListSection
          heading='Owners'
          emptySentence={LIST_EMPTY_SENTENCES.owners}
          isEmpty={owners.length === 0}
        >
          <ReadOnlyListTable
            label='Owners'
            noun='owner'
            rows={owners}
            columns={OWNER_COLUMNS}
            onGoToElement={onGoToElement}
          />
        </ListSection>
      </section>
      <ListSection
        heading='Officers'
        emptySentence={LIST_EMPTY_SENTENCES.officers}
        isEmpty={officers.length === 0}
      >
        <ReadOnlyListTable
          label='Officers'
          noun='officer'
          rows={officers}
          columns={officerColumns(taxYear)}
          onGoToElement={onGoToElement}
        />
      </ListSection>
      <ListSection
        heading='Subsidiaries and states'
        emptySentence={LIST_EMPTY_SENTENCES.subsidiariesAndStates}
        isEmpty={subsidiaries.length === 0 && stateTaxIds.length === 0}
      >
        <div className='flex flex-col gap-3'>
          {subsidiaries.length > 0 && (
            <div>
              <h4 className='text-sm font-semibold mb-1'>Subsidiaries</h4>
              <ReadOnlyListTable
                label='Subsidiaries'
                noun='subsidiary'
                rows={subsidiaries}
                columns={SUBSIDIARY_COLUMNS}
                onGoToElement={onGoToElement}
              />
            </div>
          )}
          {stateTaxIds.length > 0 && (
            <div>
              <h4 className='text-sm font-semibold mb-1'>
                State registrations
              </h4>
              <ReadOnlyListTable
                label='State registrations'
                noun='state registration'
                rows={stateTaxIds}
                columns={STATE_REGISTRATION_COLUMNS}
                onGoToElement={onGoToElement}
              />
            </div>
          )}
        </div>
      </ListSection>
    </div>
  );
}
